import { Business, BusinessArea } from '../../../interfaces/business.interface';

/**
 * Calcula la comisión total usada por todas las áreas de una empresa
 */
export const calculateUsedCommission = (areas: BusinessArea[] = []): number => {
    return areas.reduce((total, area) => total + area.commission, 0);
};

/**
 * Calcula la comisión disponible de una empresa
 */
export const calculateAvailableCommission = (business: Business): number => {
    const totalCommission = business.total_commission || 100; // Por defecto 100%
    const usedCommission = calculateUsedCommission(business.areas);
    return Math.max(0, totalCommission - usedCommission);
};

/**
 * Verifica si se puede crear un área con la comisión especificada
 */
export const canCreateAreaWithCommission = (
    business: Business,
    requestedCommission: number
): boolean => {
    const availableCommission = calculateAvailableCommission(business);
    return requestedCommission <= availableCommission && requestedCommission > 0;
};

/**
 * Calcula el porcentaje de comisión usada
 */
export const calculateCommissionUsagePercentage = (business: Business): number => {
    const totalCommission = business.total_commission || 100;
    const usedCommission = calculateUsedCommission(business.areas);
    return Math.round((usedCommission / totalCommission) * 100);
};

/**
 * Formatea la comisión para mostrar en la UI
 */
export const formatCommissionDisplay = (business: Business): string => {
    const available = calculateAvailableCommission(business);
    const used = calculateUsedCommission(business.areas);
    const total = business.total_commission || 100;

    return `${available.toFixed(1)}% disponible (${used.toFixed(1)}% / ${total}% usada)`;
};

/**
 * Encuentra el área principal de una empresa (la que tiene el mismo acrónimo)
 */
export const findMainArea = (business: Business): BusinessArea | null => {
    if (!business.areas || business.areas.length === 0) return null;
    return business.areas.find(area => area.acronym === business.acronym) || null;
};

/**
 * Verifica si se puede crear un área redistribuyendo comisiones del área principal
 */
export const canCreateAreaWithRedistribution = (
    business: Business,
    requestedCommission: number
): { canCreate: boolean; redistribution?: { mainArea: BusinessArea; newCommission: number } } => {
    // Primero verificar si hay comisión disponible normalmente
    if (canCreateAreaWithCommission(business, requestedCommission)) {
        return { canCreate: true };
    }

    // Si no hay comisión disponible, verificar si se puede redistribuir desde área principal
    const mainArea = findMainArea(business);
    if (!mainArea || mainArea.commission < requestedCommission) {
        return { canCreate: false };
    }

    // Se puede crear restando del área principal
    return {
        canCreate: true,
        redistribution: {
            mainArea,
            newCommission: mainArea.commission - requestedCommission
        }
    };
};

/**
 * Calcula las comisiones resultantes después de crear un área nueva
 */
export const calculateCommissionsAfterNewArea = (
    business: Business,
    newAreaCommission: number
): BusinessArea[] => {
    const available = calculateAvailableCommission(business);

    // Si hay comisión disponible, no es necesario redistribuir
    if (available >= newAreaCommission) {
        return business.areas || [];
    }

    // Redistribuir desde área principal
    const mainArea = findMainArea(business);
    if (!mainArea) return business.areas || [];

    const shortfall = newAreaCommission - available;
    const newMainCommission = mainArea.commission - shortfall;

    if (newMainCommission < 0) return business.areas || [];

    return (business.areas || []).map(area =>
        area.id === mainArea.id
            ? { ...area, commission: newMainCommission }
            : area
    );
}; 